// GlobalInstancePool
//
// ONE InstancedMesh per fragment hash, shared by every tile that
// instantiates that geometry. This is the change that drops the draw
// call count from "N tiles × M hashes" to "M unique hashes in scene"
// — typically 50–200 in a BIM model, vs. thousands without it.
//
// === SLOT MODEL ===
//
//   acquire(uid, instance, applicator)
//     Allocates an index in the shared InstancedMesh. Writes the
//     instance's delta-matrix into snapshotMatrices and the live
//     instanceMatrix (zero-scale if disabled by the exporter). Calls
//     the applicator to overlay any pre-existing store state
//     (hidden / isolated / highlighted for this uid). Returns the
//     slot index.
//
//   release(index)
//     Zero-scales the slot, marks the userData entry null, returns
//     the index to the free list. Slot space stays in the GPU buffer
//     (no resize), ready to be reused on the next acquire().
//
//   swapLod(lod) is a no-op for now — the pool is fixed at the LOD it
//   was built with (default = fine). Per-tile LOD picking made sense
//   when every tile had its own InstancedMesh; with shared pools an
//   LOD change would have to affect every other tile using the pool,
//   so we keep one LOD and accept the (minor) overdraw at distance.
//   If LOD pinning becomes a problem later, the fix is three parallel
//   pools per hash — one per LOD — with slot promotion between them.
//
// === GROW PATH ===
//
// Pools start at `initialCapacity` slots (default 256) and double on
// demand. Growth is the only delicate part: we build a new
// InstancedMesh at 2× capacity, copy the live attributes over, swap
// it into the scene, and dispose the old one. Anyone who held a
// reference to `pool.mesh` BEFORE the grow now sees the old mesh.
//
// To avoid that, callers should ALWAYS access the mesh through
// `pool.mesh` at use time — never cache it. The adapter and the
// applicator do this correctly because every operation routes through
// `slot.pool.mesh` (see MapMeshesAdapter / InstanceStateApplicator).
//
// `viewer.meshes` (the raycast set) is the one place that DOES retain
// the mesh directly — the grow path rewires it via the onMeshSwapped
// callback the registry installs.

import { Color, Matrix4, InstancedMesh, DynamicDrawUsage, InstancedBufferAttribute, Sphere, Vector3 } from 'three';
import { ensureGhostShader, ghostTwinMaterial, syncGhostTwin, recountGhosts } from './ghostMaterial';
import { markSceneChanged } from './renderSignal';
import { applySurfaceShading } from './surfaceShading';
import { patchStateColor } from './stateColor';
import { singlePassBlend } from './blendPass';

// Depth/blend state for a pool mesh.
//
// This used to be helpers/three.js SetChildrenDepthWrite, which decides
// `transparent = (opacity < 1)`. That rule was written for the legacy
// exporter, which put alpha in the material's base colour. Fragments put
// alpha in COLOR_0 and leave material opacity at 1, so the rule forced
// every fragment pool opaque and the viewer showed clearance zones,
// glazing and every other transparent surface as solid (SpaceX, 2026-09-14:
// 3,992 fragments / 198k instances carried alpha nobody could see).
//
// `transparent` now comes from the GLB (alphaMode BLEND, set by GLTFLoader
// and carried through the parse worker). Transparent pools do not write
// depth, as GLTFLoader intends: a see-through shell must not occlude what
// sits behind it, and pools all sit at the origin so three cannot sort
// them back-to-front anyway.
function applyPoolDepthState(mesh) {
  if (!mesh) return;
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!material) continue;
    material.colorWrite = true;
    material.depthTest = true;
    material.depthWrite = !material.transparent;
    // A blended pool is double-sided like every fragment: one draw, not
    // three's back-then-front pair (see blendPass.js).
    singlePassBlend(material);
  }
}

// Compute a bounding sphere from ONLY the active instances of a pool's
// InstancedMesh. Skips slots whose userData entry is null (released or
// never-acquired) and slots whose matrix is the ZERO_MATRIX sentinel
// (soft-hidden by hideSlots). Three.js's default
// InstancedMesh.computeBoundingSphere walks every slot up to `mesh.count`
// including the zero-scaled origin slots, which yanks the sphere center
// toward (0,0,0) and causes false-positive frustum culls when the camera
// is inside the actual building bounds but outside the bad sphere.
//
// Sphere = (centroid of active translation vectors, max distance from
// centroid + geometry's own radius). Geometry radius accounts for the
// mesh's extent around each instance center; without it, instances on
// the sphere boundary could have geometry that extends past it and still
// be visible while the sphere test reports out-of-frustum.
function _computeActiveBoundingSphere(pool, outSphere) {
  const mesh = pool.mesh;
  const matrices = mesh.userData.snapshotMatrices;
  const userData = mesh.userData;
  const count = mesh.count;
  if (count === 0) {
    outSphere.center.set(0, 0, 0);
    outSphere.radius = -1; // negative = no instances; Three.js skips render
    return;
  }
  let cx = 0, cy = 0, cz = 0;
  let activeCount = 0;
  for (let i = 0; i < count; i++) {
    if (userData[i] == null) continue;
    const base = i * 16;
    // Column-major: translation at .elements[12,13,14]. snapshotMatrices
    // is the snapshot (true desired matrix), not the live instanceMatrix
    // — so soft-hidden slots (live = ZERO) still contribute their real
    // world position here. That's the right behavior: re-show shouldn't
    // need to recompute the sphere.
    cx += matrices[base + 12];
    cy += matrices[base + 13];
    cz += matrices[base + 14];
    activeCount++;
  }
  if (activeCount === 0) {
    outSphere.center.set(0, 0, 0);
    outSphere.radius = -1;
    return;
  }
  cx /= activeCount; cy /= activeCount; cz /= activeCount;
  let maxDistSq = 0;
  for (let i = 0; i < count; i++) {
    if (userData[i] == null) continue;
    const base = i * 16;
    const dx = matrices[base + 12] - cx;
    const dy = matrices[base + 13] - cy;
    const dz = matrices[base + 14] - cz;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > maxDistSq) maxDistSq = distSq;
  }
  const maxDist = Math.sqrt(maxDistSq);
  const geom = mesh.geometry;
  if (!geom.boundingSphere) geom.computeBoundingSphere();
  const geomRadius = geom.boundingSphere ? geom.boundingSphere.radius : 0;
  outSphere.center.set(cx, cy, cz);
  outSphere.radius = maxDist + geomRadius;
}

function _markSphereDirty(pool) {
  if (pool && pool.mesh) pool._sphereDirty = true;
}

const ZERO_MATRIX = new Matrix4().makeScale(0.0001, 0.0001, 0.0001);
// Scratch for the colour reset in acquire(), so a recycled slot starts from
// its base colour rather than the previous occupant's selection tint.
const _acquireColor = new Color(1, 1, 1);

// Scratch for release(): the same invisible scale, moved to where the
// element actually was. Reused rather than allocated per release.
const _releasedMatrix = new Matrix4();
const DEFAULT_INITIAL_CAPACITY = 256;
const GROW_FACTOR = 2;

// === BATCHED needsUpdate FLUSH ===
//
// Setting `instanceMatrix.needsUpdate = true` after every individual
// acquire/release triggers a full GL buffer re-upload on the next render
// — costly when a single materialize batch writes hundreds of slots
// across multiple pools. Instead, dirty pools accumulate here and flush
// once per render frame. The streaming controller calls flushDirty()
// at the end of each materialize sweep; render-loop integration covers
// the case where someone forgets.
const _dirtyPools = new Set();
const _dirtyColorPools = new Set();

function _markMatrixDirty(pool) { _dirtyPools.add(pool); }
function _markColorDirty(pool)  { _dirtyColorPools.add(pool); }

function flushDirtyPools() {
  if (_dirtyPools.size || _dirtyColorPools.size) markSceneChanged('flushPools');
  for (const pool of _dirtyPools) {
    if (pool.mesh && pool.mesh.instanceMatrix) {
      pool.mesh.instanceMatrix.needsUpdate = true;
    }
  }
  for (const pool of _dirtyColorPools) {
    if (pool.mesh && pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;
  }
  _dirtyPools.clear();
  _dirtyColorPools.clear();
}

export { flushDirtyPools };

export default class GlobalInstancePool {
  /**
   * @param {Object} opts
   * @param {string} opts.hash               raw fragment hash
   * @param {Array}  opts.libraryParts       [lo, med, hi] LOD slots
   * @param {number} opts.initialLod         which LOD to bind initially
   * @param {Object} opts.sceneRoot          three.js Object3D, parent of the pool mesh
   * @param {number} [opts.initialCapacity]  initial slot count, default 256
   * @param {Function} [opts.onMeshSwapped]  (oldMesh, newMesh) => void
   *   Called after a grow swaps the underlying InstancedMesh. Use this
   *   to update raycast lists or any other system that holds a direct
   *   mesh reference instead of routing through pool.mesh.
   */
  constructor({
    hash, libraryParts, initialLod = 0, sceneRoot,
    initialCapacity = DEFAULT_INITIAL_CAPACITY,
    onMeshSwapped = null,
  }) {
    this.hash = hash;
    this.libraryParts = libraryParts;
    this.sceneRoot = sceneRoot;
    this.currentLod = initialLod;
    this.capacity = initialCapacity;
    this.count = 0;
    this.freeSlots = [];
    this.onMeshSwapped = onMeshSwapped;
    this.mesh = null;
    // Set of tile keys that currently have unhidden claims in this
    // pool. Drives mesh.visible — when this set is empty three.js skips
    // the entire pool draw (zero vertex work, zero fragment work).
    // This is the only thing that actually saves cost for "user hid
    // everything" or "entire pool out of frustum"; zero-scaling a
    // slot's matrix only kills rasterization, not the vertex shader.
    this.activeTiles = new Set();
    // WHICH SLOTS ARE PARKED (zero-scaled by hideSlots but still claimed).
    //
    // mesh.visible used to be driven by activeTiles alone, which is a
    // statement about TILES and not about geometry: a pool shared by many
    // tiles went fully invisible the moment the last tile to park happened
    // to empty that set, taking every other tile's live slots with it -
    // and because nothing was released and every tile stayed registered,
    // no part of the system could tell anything was missing, so nothing
    // ever brought it back. Visibility now follows LIVE UNHIDDEN SLOTS,
    // which is the thing that actually has something to draw.
    this.hiddenSlots = new Set();
    this._build();
  }

  _build() {
    const slot = this.libraryParts[this.currentLod];
    if (!slot) {
      throw new Error(
        `GlobalInstancePool ${this.hash}: no LOD slot at index ${this.currentLod}`
      );
    }

    const mesh = new InstancedMesh(slot.geometry, patchStateColor(applySurfaceShading(ensureGhostShader(slot.material))), this.capacity);
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    // Frustum culling DISABLED. The active-only boundingSphere
    // (_computeActiveBoundingSphere, kept for reference) fixes the
    // F-frame / zoom-in dropouts caused by zero-scaled released slots
    // dragging the sphere toward (0,0,0), but a tight pool sphere
    // around clustered instances still false-culls the WHOLE pool
    // when the user is close to one instance and rotates horizontally:
    // the sphere exits the frustum on the side rotated-away-from even
    // though some instances of the same hash remain visually relevant.
    // Pool-level cull at this granularity is too coarse.
    //
    // Falling back to the original strategy:
    //   1) mesh.visible = (activeTiles.size > 0) — pool draws only when
    //      at least one tile contributes unhidden slots. Skips entire
    //      pool draws with zero false negatives.
    //   2) Tile-level padded-frustum gate in streamingController
    //      decides which tiles' slots are active.
    // Accept the per-pool vertex cost; correctness wins over speed
    // until a per-region or BVH-based cull lands.
    mesh.frustumCulled = false;
    mesh.name = `pool_${this.hash.slice(0, 8)}`;

    // Zero-scale every slot up front so unused capacity isn't drawn.
    for (let i = 0; i < this.capacity; i++) mesh.setMatrixAt(i, ZERO_MATRIX);
    mesh.instanceMatrix.needsUpdate = true;

    // Per-instance color (1,1,1 default = no tint).
    const colorBuffer = new Float32Array(this.capacity * 3);
    for (let i = 0; i < this.capacity * 3; i++) colorBuffer[i] = 1;
    mesh.instanceColor = new InstancedBufferAttribute(colorBuffer, 3);

    // Per-instance ghost flag (0 = normal, 1 = see-through shell), read
    // by the shader ensureGhostShader installs. Lives on the geometry
    // like any custom instanced attribute; the applicator writes it
    // alongside the colour (see ghostMaterial.js).
    const ghostAttr = new InstancedBufferAttribute(new Float32Array(this.capacity), 1);
    ghostAttr.setUsage(DynamicDrawUsage);
    slot.geometry.setAttribute('instanceGhost', ghostAttr);

    // userData is an Array (slot index → entry) plus a handful of
    // named-property side tables. This shape is what the applicator
    // already expects from the old per-tile materializer; we preserve
    // it so the applicator's hot path needs zero changes.
    const userData = [];
    for (let i = 0; i < this.capacity; i++) userData[i] = null;
    userData.snapshotMatrices = new Float32Array(this.capacity * 16);
    userData.centeredMatrices = userData.snapshotMatrices;
    userData.baseColors       = new Float32Array(this.capacity * 3);
    for (let i = 0; i < this.capacity * 3; i++) userData.baseColors[i] = 1;
    userData.currentLod = this.currentLod;
    userData.hash       = this.hash;

    mesh.userData = userData;
    mesh.centeredMatrices = userData.snapshotMatrices;

    applyPoolDepthState(mesh);
    mesh.layers.set(0);

    // count starts at 0 so Three.js draws zero instances until we
    // begin acquiring. Saves a frame of "everything zero-scaled in
    // the depth buffer" overdraw.
    mesh.count = 0;

    // Static scene objects: pool meshes never move (instances carry the
    // transforms), so three must not recompute their world matrix every
    // frame - on SpaceX updateMatrixWorld + multiplyMatrices over ~5k
    // pool meshes was ~11 % of the main thread. Updated once after the
    // mesh joins the scene root. Fog is off on pool materials as well:
    // refreshFogUniforms per draw was another 2.4 %, and the sky dome
    // does not depend on three's fog.
    mesh.matrixAutoUpdate = false;
    mesh.matrixWorldAutoUpdate = false;
    if (slot.material && slot.material.fog !== false) { slot.material.fog = false; slot.material.needsUpdate = true; }
    // Picking: three >= r150 tests the ray against mesh.boundingSphere
    // before walking the instances, and computes that sphere ONCE (on
    // the first pick) from the instances present at the time - every
    // instance acquired later fell outside it and could not be selected
    // (r135 walked the instances with no sphere test). A pool is
    // scene-wide by nature, so give it a sphere that always passes and
    // let the per-instance walk decide, as before.
    mesh.boundingSphere = new Sphere(new Vector3(0, 0, 0), 1e9);
    this.mesh = mesh;
    if (this.sceneRoot) this.sceneRoot.add(mesh);
    mesh.updateMatrixWorld(true);
    this._attachGhostTwin(mesh, slot);
  }

  // The transparent twin that draws this pool's ghosted instances (see
  // ghostMaterial.js). It shares the solid mesh's instance buffers, so
  // nothing is written twice; it only has to be shown, hidden and
  // re-created when the solid mesh is.
  _attachGhostTwin(mesh, slot) {
    const twin = new InstancedMesh(slot.geometry, patchStateColor(applySurfaceShading(ghostTwinMaterial(slot.material))), 1);
    twin.instanceMatrix = mesh.instanceMatrix;
    twin.instanceColor = mesh.instanceColor;
    twin.frustumCulled = false;
    twin.name = mesh.name + '_ghost';
    twin.layers.set(0);
    twin.renderOrder = 1;
    twin.count = mesh.count;
    twin.visible = false;
    // Picking goes through the solid mesh; a ghost is context, not a target.
    twin.raycast = () => {};
    twin.matrixAutoUpdate = false;
    twin.matrixWorldAutoUpdate = false;
    if (twin.material && twin.material.fog !== false) { twin.material.fog = false; twin.material.needsUpdate = true; }
    mesh.__ghostTwin = twin;
    if (this.sceneRoot) this.sceneRoot.add(twin);
    twin.updateMatrixWorld(true);
    syncGhostTwin(mesh);
  }

  _detachGhostTwin(mesh) {
    const twin = mesh && mesh.__ghostTwin;
    if (!twin) return;
    if (twin.parent) twin.parent.remove(twin);
    // Buffers are the solid mesh's; only the twin object itself goes.
    twin.instanceMatrix = null;
    twin.instanceColor = null;
    if (typeof twin.dispose === 'function') { try { twin.dispose(); } catch (e) { /* shared buffers */ } }
    mesh.__ghostTwin = null;
  }

  /**
   * Allocate a slot for one instance. Returns the slot index.
   *
   * @param {string} uid                  the uniqueId for this instance
   * @param {Object} instance             sidecar entry: { uniqueId, deltaMatrix, disabled, hash, ... }
   * @param {Object|null} applicator      InstanceStateApplicator (optional)
   * @param {string|null} tileKey         which tile this slot belongs to
   */
  acquire(uid, instance, applicator, tileKey) {
    let index;
    if (this.freeSlots.length > 0) {
      index = this.freeSlots.pop();
    } else if (this.count < this.capacity) {
      index = this.count++;
    } else {
      this._grow();
      index = this.count++;
    }

    // Decode the C# row-major delta matrix into three.js column-vector
    // form. See InstanceMaterializer's class doc for the matrix
    // convention — same transpose, same convention.
    const tmp = new Matrix4();
    tmp.fromArray(instance.deltaMatrix);
    // Binary sidecar rows (cm) are already column-major; JSON rows are
    // the C# row-major layout and need the transpose.
    if (!instance.cm) tmp.transpose();

    const snap = this.mesh.userData.snapshotMatrices;
    tmp.toArray(snap, index * 16);

    // Legacy `transformation.m0..m15` field (row-major form of the
    // column-vector matrix). helpers/three.js#instanceShow reads
    // these on legacy projects' hide path; preserved for compat even
    // though the streaming path doesn't use it.
    // Memory diet (2026-09-08, SpaceX BA03: 1.4M rows took the heap to
    // 2.7 GB): the record is the sidecar row plus four small fields; the
    // 16-field legacy `transformation` object is built on demand from
    // the snapshot matrix instead of stored per slot.
    const rec = instance;
    const pool = this;
    rec.id = instance.uniqueId;
    rec.index = index;
    rec.hash = this.hash;
    rec.tileKey = tileKey;
    if (!Object.prototype.hasOwnProperty.call(rec, 'transformation')) {
      Object.defineProperty(rec, 'transformation', {
        enumerable: false, configurable: true,
        get() {
          // The pool's CURRENT snapshot: a grow replaces the array and a
          // compaction moves the row, so a captured array would go stale.
          const live = pool.mesh && pool.mesh.userData && pool.mesh.userData.snapshotMatrices;
          const s = live || snap; const o = rec.index * 16;
          return {
            m0: s[o], m1: s[o + 4], m2: s[o + 8], m3: s[o + 12],
            m4: s[o + 1], m5: s[o + 5], m6: s[o + 9], m7: s[o + 13],
            m8: s[o + 2], m9: s[o + 6], m10: s[o + 10], m11: s[o + 14],
            m12: s[o + 3], m13: s[o + 7], m14: s[o + 11], m15: s[o + 15],
          };
        },
      });
    }
    this.mesh.userData[index] = rec;

    // Register this tile as an active contributor so the pool's mesh
    // becomes visible. No-op if already in the set.
    if (tileKey) {
      this.activeTiles.add(tileKey);
      // No parked-slot walk here any more (see _updateMeshVisibility): it
      // ran once PER ACQUIRE and was 4.4-5.3 s of main thread in a BA03
      // load on a real GPU (Tom, 2026-10-13).
      this._updateMeshVisibility();
    }

    // POP-IN FIX (2026-05-13): always write ZERO_MATRIX here, even for
    // non-disabled instances. The streaming controller's post-register
    // applyAll pass will write the correct matrix (snapshot or zero)
    // based on full store state. Without this, isolate / hide / runtime-
    // disable cause visible "popping" — each hash's acquire happens in a
    // separate async tick of _materializeTile's Promise.all over
    // hashesToFetch, and the renderer paints frames between them
    // showing instances at their snapshot matrix before applyAll has a
    // chance to hide them. Initial-zero defers the visible-write
    // entirely to applyAll, so tiles appear all-at-once with correct
    // visibility state. Snapshot matrix is still written to
    // userData.snapshotMatrices above, so applyAll has the source.
    this.mesh.setMatrixAt(index, ZERO_MATRIX);

    if (this.mesh.count <= index) {
      this.mesh.count = index + 1;
      if (this.mesh.__ghostTwin) this.mesh.__ghostTwin.count = this.mesh.count;
    }
    _markMatrixDirty(this);
    _markSphereDirty(this);
    // A RECYCLED SLOT MUST NOT INHERIT THE PREVIOUS OCCUPANT'S COLOUR
    // (owner, 2026-10-05: "many of the level fragments are holding on the
    // selection colors even when they are not selected, as soon as i open a
    // level... its like a stale selection").
    //
    // IT LITERALLY IS ONE, AND IT IS PAINT ON A BUFFER RATHER THAN
    // MEMBERSHIP OF A SELECTION. That is why the second half of his report
    // follows: changing the status of a newly selected fragment does not move
    // the blue ones, because they were never in the selection - a status
    // write acts on the real set, which does not contain them.
    //
    // release() parks the matrix, nulls userData[index] and returns the index
    // to the free list. It does NOT touch instanceColor, and nothing else
    // does either: baseColors is filled with 1 at creation and never written
    // again. So the colour at this index is still the last thing written for
    // whoever lived here before.
    //
    // AND THE REPAINT ON ACQUIRE IS DEFERRED - instanceMaterializer is the
    // only caller and it passes the applicator as null on purpose, so the
    // `if (applicator)` below never runs in production. The slot's colour is
    // therefore whatever the previous occupant left until some later pass
    // happens to write it, and a pass that only visits uids with STATE never
    // will, because this uid has none.
    //
    // One write, here, where correctness is owed. Not in release(): that is
    // the teardown hot path and a released slot is invisible at 0.0001 scale,
    // so painting it there is work nobody can see. The colour attribute is
    // marked dirty on the next line either way, so the flush is already paid
    // for.
    if (this.mesh.instanceColor) {
      const base = this.mesh.userData && this.mesh.userData.baseColors;
      const at = index * 3;
      if (base && (at + 2) < base.length) {
        _acquireColor.setRGB(base[at], base[at + 1], base[at + 2]);
      } else {
        _acquireColor.setRGB(1, 1, 1);
      }
      this.mesh.setColorAt(index, _acquireColor);
      if (this.mesh.__ghostTwin && this.mesh.__ghostTwin.setColorAt) {
        this.mesh.__ghostTwin.setColorAt(index, _acquireColor);
      }
    }
    if (this.mesh.instanceColor) _markColorDirty(this);

    // Overlay any pre-existing store state for this uid. If the user
    // hid/highlighted/isolated a uid before its host tile loaded, the
    // store remembers and the applicator applies the right matrix +
    // color to this brand-new slot.
    if (applicator) applicator.applyAll(this.mesh);

    return index;
  }

  release(index) {
    if (index < 0 || index >= this.capacity) return;
    // A RELEASED SLOT STAYS WHERE ITS ELEMENT WAS. It used to be written
    // with ZERO_MATRIX, which is a pure scale with NO TRANSLATION - so a
    // released instance did not shrink in place, IT COLLAPSED TO A SPECK AT
    // THE WORLD ORIGIN. Two consequences, both real:
    //
    //   1. IT IS STILL RAYCASTABLE. userData[index] is nulled but the slot
    //      stays inside mesh.count until the TAIL happens to be trimmed, and
    //      trimCount only reclaims the tail - a released slot below a live
    //      one stays for the life of the pool. A click whose ray passes
    //      through the origin hits one, finds no userData, and the picker
    //      reports "There is no model associated with this component"
    //      (viewer/index.js, the else branch that names this exact cause:
    //      "the slot was released/regrown between materialize and click").
    //   2. IT INFLATES THE MESH'S BOUNDS. three's default
    //      computeBoundingSphere walks every slot up to count, so origin
    //      specks drag the sphere toward 0,0,0 - which is why this file
    //      carries a hand-written _computeActiveBoundingSphere. That
    //      routine is kept for reference and NOT called (pool meshes set
    //      frustumCulled = false), but the DEFAULT sphere still gates
    //      InstancedMesh.raycast, so the inflated bounds are what let a
    //      stray ray reach the specks in the first place.
    //
    // The position comes from snapshotMatrices rather than from the visible
    // matrix, because a slot that was HIDDEN before being released already
    // has a zeroed visible matrix - reading that would put the speck back at
    // the origin for exactly the slots most likely to be released.
    //
    // THE SCALE IS UNCHANGED at 0.0001. It is not zero on purpose: a true
    // zero makes a degenerate matrix and can produce NaNs in a bounds
    // computation. Only the translation is being restored here.
    // ACQUIRE IS NOT TOUCHED: the pop-in fix of 2026-05-13 writes
    // ZERO_MATRIX at acquire so an instance stays invisible until applyAll
    // decides its real matrix, and that window is one materialisation long.
    // This is about slots that persist.
    const snap = this.mesh.userData && this.mesh.userData.snapshotMatrices;
    if (snap && snap.length >= (index + 1) * 16) {
      const o = index * 16;
      _releasedMatrix.copy(ZERO_MATRIX);
      _releasedMatrix.elements[12] = snap[o + 12];
      _releasedMatrix.elements[13] = snap[o + 13];
      _releasedMatrix.elements[14] = snap[o + 14];
      this.mesh.setMatrixAt(index, _releasedMatrix);
    } else {
      this.mesh.setMatrixAt(index, ZERO_MATRIX);
    }
    this.mesh.userData[index] = null;
    this.freeSlots.push(index);
    // A released slot is free, not parked. Left in hiddenSlots it would
    // keep counting against visibility for ever.
    this.hiddenSlots.delete(index);
    this._needsTrim = true;
    _markMatrixDirty(this);
    _markSphereDirty(this);
  }

  // Freed slots are drawn at zero scale: the GPU still runs their
  // vertices (SpaceX: ~1.3M triangles per frame the budget did not
  // count after a quality pass). Pull mesh.count back to the last live
  // slot when the tail is free and drop those indices from the free
  // list so acquire cannot hand one out twice. Called from the
  // registry's eviction tick, not per release.
  // How many slots below mesh.count are free (holes the GPU still runs
  // the vertex shader for, at zero scale).
  holeCount() {
    if (!this.freeSlots) return 0;
    return this.freeSlots.length;
  }

  // CLOSE THE HOLES. A released slot below the last live one stays inside
  // mesh.count for the life of the pool (trimCount only reclaims the tail),
  // and an InstancedMesh draws every instance up to count, zero-scaled or
  // not: on staging BA03 at rest 14-25k hole slots, ~2.4 M triangles of
  // vertex work and of renderer.info.triangles, outside the budget.
  //
  // Live slots move DOWN into the holes, in index order, so a slot is only
  // ever written after its old occupant has moved (k <= src always). Every
  // per-slot buffer moves with it: snapshot and visible matrices, colour,
  // base colour, ghost flag, the parked set and the row record's index.
  // Returns Map(oldIndex -> newIndex) for the slots that moved; the CALLER
  // must rewrite every outside reference (tile claims, adapter slots and
  // records) before anything else reads an index - see
  // StreamingController._compactPools, which only runs when nothing is
  // materialising.
  compact() {
    const mesh = this.mesh;
    if (!mesh || !mesh.userData || this.freeSlots.length === 0) return new Map();
    const ud = mesh.userData;
    const snap = ud.snapshotMatrices;
    const base = ud.baseColors;
    const im = mesh.instanceMatrix && mesh.instanceMatrix.array;
    const ic = mesh.instanceColor && mesh.instanceColor.array;
    const ghost = mesh.geometry && mesh.geometry.getAttribute && mesh.geometry.getAttribute('instanceGhost');
    const ga = ghost && ghost.array;
    const remap = new Map();
    let k = 0;
    for (let src = 0; src < this.count; src++) {
      const rec = ud[src];
      if (!rec) continue;
      if (src !== k) {
        if (snap) snap.copyWithin(k * 16, src * 16, src * 16 + 16);
        if (im) im.copyWithin(k * 16, src * 16, src * 16 + 16);
        if (ic) ic.copyWithin(k * 3, src * 3, src * 3 + 3);
        if (base) base.copyWithin(k * 3, src * 3, src * 3 + 3);
        if (ga && src < ga.length && k < ga.length) { ga[k] = ga[src]; ga[src] = 0; }
        ud[k] = rec;
        ud[src] = null;
        if (rec && typeof rec === 'object') rec.index = k;
        if (this.hiddenSlots.delete(src)) this.hiddenSlots.add(k);
        remap.set(src, k);
      }
      k++;
    }
    // Everything at or past the new count is free: park it at zero scale
    // (not drawn any more, but a later acquire must not inherit it).
    for (let i = k; i < this.count; i++) {
      mesh.setMatrixAt(i, ZERO_MATRIX);
      ud[i] = null;
      this.hiddenSlots.delete(i);
    }
    this.count = k;
    this.freeSlots = [];
    this._needsTrim = false;
    mesh.count = k;
    if (mesh.__ghostTwin) mesh.__ghostTwin.count = k;
    if (mesh.instanceMatrix) { if (mesh.instanceMatrix.clearUpdateRanges) mesh.instanceMatrix.clearUpdateRanges(); mesh.instanceMatrix.needsUpdate = true; }
    if (mesh.instanceColor) { if (mesh.instanceColor.clearUpdateRanges) mesh.instanceColor.clearUpdateRanges(); mesh.instanceColor.needsUpdate = true; }
    if (ghost) ghost.needsUpdate = true;
    _markMatrixDirty(this);
    _markSphereDirty(this);
    this._updateMeshVisibility();
    return remap;
  }

  trimCount() {
    if (!this._needsTrim || !this.mesh) return 0;
    this._needsTrim = false;
    const ud = this.mesh.userData;
    let n = this.count;
    while (n > 0 && !ud[n - 1]) n--;
    if (n === this.count) return 0;
    const trimmed = this.count - n;
    this.count = n;
    this.freeSlots = this.freeSlots.filter((i) => i < n);
    this.hiddenSlots.forEach((i) => { if (i >= n) this.hiddenSlots.delete(i); });
    this.mesh.count = n;
    if (this.mesh.__ghostTwin) this.mesh.__ghostTwin.count = n;
    _markMatrixDirty(this);
    _markSphereDirty(this);
    return trimmed;
  }

  // Soft-hide slots without releasing them — used by the streaming
  // controller's tile-level frustum cull. Slots stay claimed (userData
  // entry intact, free list untouched), but the visible instanceMatrix
  // is zero-scale so three.js draws nothing. Restoring is showSlots().
  //
  // The tileKey arg drops this tile from `activeTiles` so when every
  // tile claiming slots in this pool is hidden, mesh.visible flips to
  // false — that's the only way to skip vertex shader work; zero-scale
  // matrices still process through vertex shading.
  hideSlots(indices, tileKey = null) {
    if (!indices || indices.length === 0) return;
    for (const idx of indices) {
      if (idx >= 0 && idx < this.capacity) {
        this.mesh.setMatrixAt(idx, ZERO_MATRIX);
        this.hiddenSlots.add(idx);
      }
    }
    if (tileKey) this.activeTiles.delete(tileKey);
    // Recomputed from the slots every time, not only when the tile set
    // changed: parking the last live slot of a pool several tiles share
    // must turn it off, and parking one of many must not.
    this._updateMeshVisibility();
    _markMatrixDirty(this);
    // Note: _sphereDirty is NOT set here. snapshotMatrices is the source
    // for the sphere, and hideSlots only flips the live instanceMatrix
    // (snapshot is untouched). Show/hide cycles don't change the sphere.
  }

  // Restore slot matrices from the snapshot buffer. Used by the
  // streaming controller when a frustum-hidden tile re-enters view.
  // After this the applicator should run again to overlay any
  // hide/isolate/highlight state that was set while hidden.
  // -> 'restored' | 'empty' | 'failed'
  //
  // IT REPORTS WHAT IT DID, and that return value is the whole fix for the
  // worst defect in this file. The caller (StreamingController._frustumShow)
  // used to clear the tile's "hidden" flag unconditionally after calling
  // this, while this had two early returns that did no work at all - so a
  // restore that silently did nothing was recorded as a restore that
  // succeeded. And since _frustumShow opens with "if (!tile._frustumHidden)
  // return", the tile could never be retried: ONE failed restore parked
  // those fragments for the lifetime of the page, with every count still
  // reporting them resident and nothing able to discover the loss. That is
  // why the owner's fragments "stay gone until reload".
  //
  // The three answers are distinguished because they need different
  // handling: 'empty' is nothing to do (do not retry), 'failed' is a real
  // failure (do retry, and do NOT tick the tile off), 'restored' is done.
  showSlots(indices, tileKey = null) {
    if (!indices || indices.length === 0) return 'empty';
    if (!this.mesh || !this.mesh.userData) return 'failed';
    const snap = this.mesh.userData.snapshotMatrices;
    if (!snap) return 'failed';
    const tmp = new Matrix4();
    let restored = 0;
    for (const idx of indices) {
      if (idx < 0 || idx >= this.capacity) continue;
      tmp.fromArray(snap, idx * 16);
      this.mesh.setMatrixAt(idx, tmp);
      this.hiddenSlots.delete(idx);
      restored += 1;
    }
    if (tileKey) this.activeTiles.add(tileKey);
    this._updateMeshVisibility();
    _markMatrixDirty(this);
    // Indices that were all out of range are not a restore: the claim
    // does not match this pool any more.
    return restored > 0 ? 'restored' : 'failed';
  }

  // Remove a tile from the active set when its slots are released
  // (full unload). Caller must invoke this BEFORE releasing the slots
  // (or just after — order doesn't matter, releases don't touch
  // activeTiles).
  // Release everything this pool owns on the GPU and detach its mesh.
  // Only called by the registry for pools with no live slot and no
  // active tile (see InstancePoolRegistry.disposeEmptyPools). The
  // geometry belongs to the library cache and is left alone.
  dispose() {
    const mesh = this.mesh;
    if (mesh) {
      this._detachGhostTwin(mesh);
      if (mesh.parent) mesh.parent.remove(mesh);
      try { if (typeof mesh.dispose === 'function') mesh.dispose(); } catch (e) { /* buffers may be shared */ }
      mesh.userData = null;
    }
    this.mesh = null;
    this.freeSlots = [];
    this.count = 0;
    this.activeTiles = new Set();
    this.disposed = true;
  }

  removeTile(tileKey) {
    if (!tileKey) return;
    if (this.activeTiles.delete(tileKey)) {
      this._updateMeshVisibility();
    }
  }

  // Toggle mesh.visible based on whether any tile currently contributes
  // unhidden slots. Three.js skips invisible meshes entirely — no draw
  // call, no vertex shader, no bounding sphere check, no nothing. This
  // is the single biggest perf lever for "user hid everything" and
  // "camera is looking away from the building" cases.
  // How many slots are claimed AND not parked - the only number that says
  // whether this pool has anything to draw.
  liveUnhiddenCount() {
    this._parkedScans = (this._parkedScans || 0) + 1;
    const claimed = (this.count || 0) - (this.freeSlots ? this.freeSlots.length : 0);
    let parked = 0;
    // A parked index that has since been released is free, not parked;
    // release() drops it from the set, and this guard keeps the count
    // honest even if some future path forgets to.
    this.hiddenSlots.forEach((i) => { if (i < this.count && this.mesh && this.mesh.userData && this.mesh.userData[i]) parked += 1; });
    return Math.max(0, claimed - parked);
  }

  _updateMeshVisibility() {
    if (!this.mesh) return;
    // EXACT WITHOUT THE WALK in the common case: a parked slot is a claimed
    // slot, so parked <= hiddenSlots.size, and more claimed slots than that
    // means at least one is live. Only a pool that is mostly parked pays for
    // the count (and then it is the honest answer, as before).
    const claimed = (this.count || 0) - (this.freeSlots ? this.freeSlots.length : 0);
    const shouldBeVisible = claimed > this.hiddenSlots.size ? true : this.liveUnhiddenCount() > 0;
    this._setMeshVisible(shouldBeVisible);
  }

  _setMeshVisible(shouldBeVisible) {
    if (!this.mesh) return;
    if (this.mesh.visible !== shouldBeVisible) {
      this.mesh.visible = shouldBeVisible;
    }
    syncGhostTwin(this.mesh);
  }

  _grow() {
    const oldMesh = this.mesh;
    const newCap = this.capacity * GROW_FACTOR;
    const slot = this.libraryParts[this.currentLod];

    // The same two patches the first mesh got. The material object is
    // shared, so it usually carries them already - but a pool that grows
    // must not be the one place where that is assumed rather than done.
    const newMesh = new InstancedMesh(slot.geometry, patchStateColor(applySurfaceShading(ensureGhostShader(slot.material))), newCap);
    newMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    newMesh.frustumCulled = false;
    newMesh.matrixAutoUpdate = false;
    newMesh.matrixWorldAutoUpdate = false;
    newMesh.boundingSphere = new Sphere(new Vector3(0, 0, 0), 1e9);
    newMesh.name = oldMesh.name;

    // Copy live instance buffer (matrices) then zero-init the new tail.
    newMesh.instanceMatrix.array.set(oldMesh.instanceMatrix.array, 0);
    for (let i = this.capacity; i < newCap; i++) {
      newMesh.setMatrixAt(i, ZERO_MATRIX);
    }
    newMesh.instanceMatrix.needsUpdate = true;

    // Copy instance colors.
    const newColorBuffer = new Float32Array(newCap * 3);
    for (let i = 0; i < newCap * 3; i++) newColorBuffer[i] = 1;
    if (oldMesh.instanceColor) {
      newColorBuffer.set(oldMesh.instanceColor.array, 0);
    }
    newMesh.instanceColor = new InstancedBufferAttribute(newColorBuffer, 3);

    // Carry the ghost flags forward at their indices; the new tail is 0.
    const oldGhost = slot.geometry.getAttribute && slot.geometry.getAttribute('instanceGhost');
    const newGhostBuffer = new Float32Array(newCap);
    if (oldGhost && oldGhost.array) newGhostBuffer.set(oldGhost.array.subarray(0, Math.min(oldGhost.array.length, newCap)), 0);
    const newGhost = new InstancedBufferAttribute(newGhostBuffer, 1);
    newGhost.setUsage(DynamicDrawUsage);
    slot.geometry.setAttribute('instanceGhost', newGhost);
    recountGhosts(newGhost);

    // Carry userData forward. The Array shape (slot index → entry +
    // named property side tables) is what the applicator expects, so
    // we preserve it bit-for-bit at the existing indices and null the
    // new tail.
    const newUserData = [];
    for (let i = 0; i < this.capacity; i++) {
      newUserData[i] = oldMesh.userData[i] || null;
    }
    for (let i = this.capacity; i < newCap; i++) newUserData[i] = null;

    const newSnapshot = new Float32Array(newCap * 16);
    newSnapshot.set(oldMesh.userData.snapshotMatrices, 0);

    const newBaseColors = new Float32Array(newCap * 3);
    for (let i = 0; i < newCap * 3; i++) newBaseColors[i] = 1;
    newBaseColors.set(oldMesh.userData.baseColors, 0);

    newUserData.snapshotMatrices = newSnapshot;
    newUserData.centeredMatrices = newSnapshot;
    newUserData.baseColors       = newBaseColors;
    newUserData.currentLod       = this.currentLod;
    newUserData.hash             = this.hash;

    newMesh.userData = newUserData;
    newMesh.centeredMatrices = newSnapshot;
    applyPoolDepthState(newMesh);
    newMesh.layers.set(0);
    newMesh.count = oldMesh.count;

    // Swap in the scene graph.
    // ATTACHED ON THE SAME CONDITION AS THE FIRST MESH, which is sceneRoot -
    // not on whether the OLD mesh happened to have a parent.
    //
    // The old form skipped `sceneRoot.add(newMesh)` whenever oldMesh was
    // detached, while still reassigning this.mesh, disposing oldMesh and
    // rewiring every cached reference - leaving the pool holding live
    // geometry that was in no scene, with every count reporting it
    // resident. Nothing detaches a pool mesh on purpose: the only path is
    // dispose(), which also nulls this.mesh and sets disposed, so an
    // oldMesh that exists here belongs in the scene by definition.
    this._detachGhostTwin(oldMesh);
    if (oldMesh.parent) oldMesh.parent.remove(oldMesh);
    if (this.sceneRoot) this.sceneRoot.add(newMesh);
    newMesh.updateMatrixWorld(true);

    this.mesh = newMesh;
    this.capacity = newCap;
    this._attachGhostTwin(newMesh, slot);

    // Free old GPU buffers.
    if (oldMesh.instanceMatrix?.dispose) oldMesh.instanceMatrix.dispose();
    if (oldMesh.instanceColor?.dispose)  oldMesh.instanceColor.dispose();
    if (typeof oldMesh.dispose === 'function') oldMesh.dispose();

    if (this.onMeshSwapped) this.onMeshSwapped(oldMesh, newMesh);

    // Diagnostic — grow should be rare; if we see it logging often,
    // bump DEFAULT_INITIAL_CAPACITY.
    console.warn(
      `[GlobalInstancePool] ${this.hash} grew to ${newCap} (count=${this.count})`
    );
  }

  // For visibility from the streaming controller / debugger.
  stats() {
    return {
      hash: this.hash,
      capacity: this.capacity,
      count: this.count,
      free: this.freeSlots.length,
      live: this.count - this.freeSlots.length,
    };
  }

  // Throwaway helper. Not really used since acquire writes the color
  // through the InstancedMesh attribute directly, but kept for the
  // applicator's _applyColorOnly path when it wants to fall back to a
  // base color without going through highlight.
  static defaultColor() { return new Color(1, 1, 1); }
}
